import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Lightbulb, Wrench, Search, Heart, Target, Megaphone, AlertTriangle, CheckCircle, Users, TrendingUp } from 'lucide-react';

const TeamCompositionDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const dimensionInfo = {
    B: { name: 'Brainstorm', icon: Lightbulb, color: 'bg-purple-500', lightBg: 'bg-purple-100', text: 'text-purple-700' },
    U: { name: 'Uncover', icon: Wrench, color: 'bg-blue-500', lightBg: 'bg-blue-100', text: 'text-blue-700' },
    I: { name: 'Inspect', icon: Search, color: 'bg-green-500', lightBg: 'bg-green-100', text: 'text-green-700' },
    L: { name: 'Lift', icon: Heart, color: 'bg-pink-500', lightBg: 'bg-pink-100', text: 'text-pink-700' },
    D: { name: 'Deliver', icon: Target, color: 'bg-orange-500', lightBg: 'bg-orange-100', text: 'text-orange-700' },
    R: { name: 'Rally', icon: Megaphone, color: 'bg-red-500', lightBg: 'bg-red-100', text: 'text-red-700' }
  };

  useEffect(() => {
    fetchComposition();
  }, []);

  const fetchComposition = async () => {
    try {
      const response = await axios.get(`${API}/admin/team-composition`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load team composition');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-2 text-gray-500">Loading team composition...</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const maxComposition = Math.max(...Object.values(data.composition));
  const sortedDimensions = Object.entries(data.composition).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
            <div className="text-2xl font-bold">{data.total_users}</div>
            <div className="text-sm text-gray-500">Total Members</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold">{data.total_assessed}</div>
            <div className="text-sm text-gray-500">Assessed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold">{data.assessment_rate}%</div>
            <div className="text-sm text-gray-500">Assessment Rate</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className={`h-8 w-8 mx-auto mb-2 ${data.gaps.length > 0 ? 'text-amber-500' : 'text-green-500'}`} />
            <div className="text-2xl font-bold">{data.gaps.length}</div>
            <div className="text-sm text-gray-500">Dimension Gaps</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Composition Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Composition by Primary Dimension
          </CardTitle>
          <CardDescription>
            Distribution of primary strengths across your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {sortedDimensions.map(([dim, count]) => {
              const info = dimensionInfo[dim];
              const Icon = info.icon;
              const pct = data.composition_percentage[dim];
              const isGap = data.gaps.includes(dim);
              const isStrength = data.strengths.includes(dim);
              
              return (
                <div key={dim} className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${info.lightBg} flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${info.text}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{info.name}</span>
                        {isGap && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                            Gap
                          </Badge>
                        )}
                        {isStrength && (
                          <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                            Strength
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {count} ({pct}%)
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${info.color} transition-all duration-500`}
                        style={{ width: `${(count / maxComposition) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Gap Analysis & Recommendations */}
      {data.recommendations.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Gap Analysis & Recommendations
            </CardTitle>
            <CardDescription className="text-amber-700">
              Areas where your team could benefit from additional strengths
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.recommendations.map((rec, idx) => {
                const info = dimensionInfo[rec.dimension];
                const Icon = info.icon;
                return (
                  <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                    <div className={`p-2 rounded-lg ${info.lightBg}`}>
                      <Icon className={`h-5 w-5 ${info.text}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{rec.name} Needed</p>
                      <p className="text-sm text-gray-600">{rec.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Pairings */}
      <Card>
        <CardHeader>
          <CardTitle>Top Pairing Codes</CardTitle>
          <CardDescription>Most common strength combinations in your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.pairing_distribution).slice(0, 10).map(([pairing, count]) => (
              <Badge 
                key={pairing} 
                variant="outline" 
                className="text-lg px-4 py-2 bg-indigo-50 border-indigo-200"
              >
                <span className="font-bold text-indigo-700">{pairing}</span>
                <span className="ml-2 text-gray-500">({count})</span>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamCompositionDashboard;
